import { useState, useMemo, useCallback } from 'react';
import { selectState } from 'helpers/filterHelper';
import * as constants from '../constants';

/**
 * Custom hook to manage event type selection state
 * Handles the logic for "no visual selection but send all types" vs "user selected specific types"
 *
 * @param {Array} currentEventTypes - Current event types from filters
 * @param {Function} setFilters - Function to update filters
 * @returns {Object} Event type selection state and handlers
 */
export const useEventTypeSelection = (currentEventTypes, setFilters, isGatewayTab = false) => {
    const [userSelectedEventTypes, setUserSelectedEventTypes] = useState(false);

    // Determine the severity codes to send in API payload
    const apiSeverityCodes = useMemo(() => {
        // If user hasn't manually selected types, send all default types for the current tab
        if (!userSelectedEventTypes) {
            if (isGatewayTab) {
                // SmartSensor DCU tab
                return [
                    constants.EventTypeIDs.DEBUG,
                    constants.EventTypeIDs.WARNING,
                    constants.EventTypeIDs.INFO
                ];
            }
            return [
                constants.EventTypeIDs.FAULT,
                constants.EventTypeIDs.WARNING,
                constants.EventTypeIDs.INFO
            ];
        }
        // Otherwise send the user's selection
        return currentEventTypes || [];
    }, [userSelectedEventTypes, currentEventTypes, isGatewayTab]);

    // Display only user-selected event types, empty if no manual selection
    const displayedEventTypes = useMemo(() => {
        return userSelectedEventTypes ? currentEventTypes || [] : [];
    }, [userSelectedEventTypes, currentEventTypes]);

    // Handle event type filter click
    const handleFilterByEventType = useCallback((typeID) => {
        setUserSelectedEventTypes(true);

        // Get current event types, default to empty array
        const eventTypes = currentEventTypes || [];
        let newEventTypes;

        // If currently empty (no user selection yet), add the clicked type
        if (!userSelectedEventTypes || eventTypes.length === 0) {
            newEventTypes = [typeID];
        } else {
            // Otherwise, toggle the type
            newEventTypes = selectState(eventTypes, typeID);
        }

        setFilters({ eventTypes: newEventTypes });
    }, [currentEventTypes, userSelectedEventTypes, setFilters]);

    // Reset selection state
    const resetSelection = useCallback(() => {
        setUserSelectedEventTypes(false);
    }, []);

    return {
        apiSeverityCodes,
        displayedEventTypes,
        handleFilterByEventType,
        resetSelection,
        userSelectedEventTypes
    };
};
